// FILE: src/admin/pages/support/owners/tabs/SupportOwnerInboxTab.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { listSupportOwnerInbox } from "../../../../../api/adminSupportApi";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function safeStr(x) {
  return x == null ? "" : String(x);
}

function upper(x) {
  const s = safeStr(x).trim();
  return s ? s.toUpperCase() : "";
}

function num(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}

function money(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

/** Clean date: "7 Feb 2026, 12:12 PM" (12h, month short, full year) */
function dtClean(v) {
  if (!v) return "—";
  try {
    const d = new Date(String(v));
    if (Number.isNaN(d.getTime())) return safeStr(v);
    const datePart = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    const timePart = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
    return `${datePart}, ${timePart}`;
  } catch (_) {
    return safeStr(v);
  }
}

/** Build a parseable ISO-ish datetime from separate date+time strings and format with dtClean */
function dtFromParts(dateStr, timeStr) {
  const d = safeStr(dateStr).trim();
  const t = safeStr(timeStr).trim();

  if (!d && !t) return "—";

  // If backend already gave a full timestamp in either field
  if (d.includes("T")) return dtClean(d);
  if (t.includes("T")) return dtClean(t);

  // Common case: "YYYY-MM-DD" + "HH:mm" or "HH:mm:ss"
  if (d && t) {
    const withSeconds = t.length === 5 ? `${t}:00` : t; // HH:mm -> HH:mm:00
    return dtClean(`${d}T${withSeconds}`);
  }

  // Fallback: format what we have
  return dtClean(d || t);
}

function Chip({ children, tone = "neutral", className = "" }) {
  const cls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50 text-amber-900"
      : tone === "bad"
      ? "border-rose-200 bg-rose-50 text-rose-800"
      : "border-slate-200 bg-slate-50 text-slate-700";

  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-md border px-2.5 py-1 text-[11px] font-medium", // ⬅️ not bold
        cls,
        className
      )}
    >
      {children}
    </span>
  );
}

/* -------------------- shared footer (your preferred style) -------------------- */
function Footer({ page, size, total, totalPages, setPage, setSize }) {
  const from = total ? page * size + 1 : 0;
  const to = total ? Math.min(total, (page + 1) * size) : 0;

  return (
    <div className="px-5 py-3 border-t border-slate-200 bg-white">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="text-xs text-slate-500">
          {total ? (
            <>
              Showing <span className="font-semibold text-slate-700">{from}</span>–<span className="font-semibold text-slate-700">{to}</span> of{" "}
              <span className="font-semibold text-slate-700">{total}</span>
            </>
          ) : (
            "No records"
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="text-xs text-slate-500">View</div>
            <div className="relative">
              <select
                value={size}
                onChange={(e) => setSize(Number(e.target.value))}
                className="appearance-none h-10 rounded-md border border-slate-200 bg-white pl-3 pr-9 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                {[10, 20, 30, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>
              <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">▼</span>
            </div>
          </div>

          <div className="text-sm text-slate-700">
            Page <span className="font-semibold">{page + 1}</span> of{" "}
            <span className="font-semibold">{Math.max(1, totalPages || 1)}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
              disabled={page <= 0}
              onClick={() => setPage(page - 1)}
            >
              Prev
            </button>
            <button
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
              disabled={totalPages ? page >= totalPages - 1 : true}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const STATUS_FILTERS = [
  { key: "ALL", label: "All" },
  { key: "PENDING", label: "Pending" },
  { key: "ACCEPTED", label: "Accepted" },
  { key: "DECLINED", label: "Declined" },
  { key: "EXPIRED", label: "Expired" },
];

/* -------------------- Drawer UI -------------------- */
function Drawer({ open, onClose, row }) {
  if (!open) return null;

  const offerStatus = upper(row?.status);
  const reqStatus = upper(row?.requestStatus);
  const payStatus = upper(row?.requestPaymentStatus);

  const offerTone =
    offerStatus === "ACCEPTED" ? "good" : offerStatus === "PENDING" ? "warn" : offerStatus === "DECLINED" ? "bad" : "neutral";
  const reqTone = reqStatus === "LIVE" ? "warn" : reqStatus === "FULFILLED" ? "good" : reqStatus === "EXPIRED" ? "bad" : "neutral";
  const payTone = payStatus === "PAID" ? "good" : payStatus === "UNPAID" ? "warn" : "neutral";

  const destinations = row?.destination || (Array.isArray(row?.destinations) ? row.destinations.join(", ") : "");

  const pickupDT = dtFromParts(row?.pickupDate, row?.pickupTime);
  const dropDT = dtFromParts(row?.dropDate, row?.dropTime);

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} />

      {/* ⬅️ wider drawer */}
      <div className="absolute right-0 top-0 h-full w-full sm:w-[740px] lg:w-[820px] bg-white shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs text-slate-500">Offer details</div>
            <div className="mt-0.5 text-base font-extrabold text-slate-900 truncate">
              Request #{row?.requestId ?? "—"} · Match #{row?.matchId ?? "—"}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <Chip tone={offerTone}>{offerStatus || "—"}</Chip>
              <Chip tone={reqTone}>{reqStatus || "—"}</Chip>
              <Chip tone={payTone}>{payStatus || "—"}</Chip>
            </div>
          </div>

          <button
            onClick={onClose}
            className="h-9 px-3 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50"
            aria-label="Close"
          >
            Close
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* IDs */}
          <section className="px-5 py-4">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600">IDs</div>
            <div className="mt-3 grid grid-cols-1 gap-3">
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-xs text-slate-500">Request ID</div>
                <div className="mt-1 text-sm font-extrabold text-slate-900 tabular-nums">{row?.requestId ?? "—"}</div>
              </div>

              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-xs text-slate-500">Match ID</div>
                <div className="mt-1 text-sm font-extrabold text-slate-900 tabular-nums">{row?.matchId ?? "—"}</div>
                <div className="mt-1 text-[11px] text-slate-500">Batch {row?.batchNo ?? "—"}</div>
              </div>

              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-xs text-slate-500">Vehicle Reg</div>
                <div className="mt-1 text-sm font-extrabold text-slate-900 tabular-nums">{row?.vehicleRegNo ?? "—"}</div>
              </div>
            </div>
          </section>

          {/* Money + Schedule */}
          <section className="px-5 pb-4">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Money & Schedule</div>
            <div className="mt-3 rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-slate-500">Fare</div>
                  <div className="text-sm font-extrabold text-slate-900 tabular-nums">₹ {money(row?.requestFare)}</div>
                </div>
              </div>

              <div className="px-4 py-3">
                <div className="grid grid-cols-1 gap-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs text-slate-500">Pickup</div>
                    <div className="text-xs font-semibold text-slate-800 tabular-nums">{pickupDT}</div>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs text-slate-500">Drop</div>
                    <div className="text-xs font-semibold text-slate-800 tabular-nums">{dropDT}</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Route */}
          <section className="px-5 pb-4">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Route</div>
            <div className="mt-3 rounded-2xl border border-slate-200 p-4">
              <div className="text-xs text-slate-500">Pickup</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{row?.pickupLocation || "—"}</div>

              <div className="mt-4 text-xs text-slate-500">Destination</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{destinations || "—"}</div>
            </div>
          </section>

          {/* Activity + Note */}
          <section className="px-5 pb-6">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Activity</div>
            <div className="mt-3 rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200">
                <div className="grid grid-cols-1 gap-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs text-slate-500">Created</div>
                    <div className="text-xs font-semibold text-slate-800">{dtClean(row?.createdAt)}</div>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs text-slate-500">Responded</div>
                    <div className="text-xs font-semibold text-slate-800">{dtClean(row?.respondedAt)}</div>
                  </div>
                </div>
              </div>

              <div className="px-4 py-3">
                <div className="text-xs text-slate-500">Note</div>
                <div className="mt-1 text-sm text-slate-900 whitespace-pre-wrap">{row?.note || "—"}</div>
              </div>
            </div>
          </section>
        </div>

        <div className="px-5 py-4 border-t border-slate-200 bg-white">
          <button
            onClick={onClose}
            className="w-full h-10 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SupportOwnerInboxTab() {
  const { ownerId } = useOutletContext();

  const [status, setStatus] = useState("ALL");
  const [q, setQ] = useState("");

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(30);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const debounceRef = useRef(null);

  // drawer state
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    setPage(0);
  }, [status, q, size]);

  useEffect(() => {
    let alive = true;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setBusy(true);
      setErr("");
      try {
        const res = await listSupportOwnerInbox({ ownerId, status, q, page, size });
        if (!alive) return;

        const content = Array.isArray(res?.content) ? res.content : [];
        setRows(content);
        setTotal(num(res?.totalElements ?? content.length ?? 0));
        setTotalPages(num(res?.totalPages ?? 0));
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Failed to load inbox offers");
        setRows([]);
        setTotal(0);
        setTotalPages(0);
      } finally {
        if (alive) setBusy(false);
      }
    }, 250);

    return () => {
      alive = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [ownerId, status, q, page, size]);

  const colgroup = useMemo(
    () => (
      <colgroup>
        <col style={{ width: "160px" }} /> {/* Request */}
        <col style={{ width: "140px" }} /> {/* Match */}
        <col style={{ width: "180px" }} /> {/* Vehicle */}
        <col style={{ width: "170px" }} /> {/* Offer */}
        <col style={{ width: "240px" }} /> {/* Req/Pay */}
        <col style={{ width: "140px" }} /> {/* Fare */}
        <col style={{ width: "220px" }} /> {/* Last activity */}
      </colgroup>
    ),
    []
  );

  function openDrawer(r) {
    setActive(r || null);
    setOpen(true);
  }

  function closeDrawer() {
    setOpen(false);
    setActive(null);
  }

  return (
    <div className="min-w-0">
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="border-b border-slate-200 px-5 py-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="text-sm font-extrabold text-slate-900">Inbox Offers</div>
              <div className="mt-1 text-xs text-slate-500">Click any row to open full request details.</div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                {STATUS_FILTERS.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>

              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search requestId / matchId / regNo"
                className="h-10 w-full sm:w-[320px] rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 placeholder:text-slate-400 outline-none"
              />
            </div>
          </div>
        </div>

        {err ? <div className="px-5 py-3 border-b border-slate-200 text-sm font-semibold text-rose-600">{err}</div> : null}

        <div className="w-full overflow-x-auto">
          <table className="min-w-[1130px] w-full table-fixed">
            {colgroup}
            <thead className="bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Request</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Match</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Vehicle</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Offer</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Request / Pay</th>
                <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-600">Fare</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Last activity</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200">
              {busy ? (
                <tr>
                  <td colSpan={7} className="px-5 py-8 text-sm text-slate-600">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-10 text-center">
                    <div className="text-sm text-slate-900">No offers</div>
                    <div className="mt-1 text-xs text-slate-500">This owner has no inbox offers for current filters.</div>
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => {
                  const offerStatus = upper(r.status);
                  const reqStatus = upper(r.requestStatus);
                  const payStatus = upper(r.requestPaymentStatus);

                  const offerTone =
                    offerStatus === "ACCEPTED" ? "good" : offerStatus === "PENDING" ? "warn" : offerStatus === "DECLINED" ? "bad" : "neutral";
                  const reqTone = reqStatus === "LIVE" ? "warn" : reqStatus === "FULFILLED" ? "good" : reqStatus === "EXPIRED" ? "bad" : "neutral";
                  const payTone = payStatus === "PAID" ? "good" : payStatus === "UNPAID" ? "warn" : "neutral";

                  const lastTs = r.respondedAt || r.createdAt;

                  return (
                    <tr
                      key={`${safeStr(r.matchId) || "m"}-${safeStr(r.vehicleRegNo) || "v"}-${idx}`}
                      className={classNames("cursor-pointer hover:bg-slate-50", idx % 2 === 0 ? "bg-white" : "bg-slate-50/30")}
                      onClick={() => openDrawer(r)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") openDrawer(r);
                      }}
                      title="Open details"
                    >
                      {/* Request (no bold) */}
                      <td className="px-4 py-4">
                        <div className="text-sm text-slate-900 tabular-nums">#{r.requestId ?? "—"}</div>
                      </td>

                      {/* Match (no bold) */}
                      <td className="px-4 py-4">
                        <div className="text-sm text-slate-900 tabular-nums">#{r.matchId ?? "—"}</div>
                      </td>

                      {/* Vehicle (no bold) */}
                      <td className="px-4 py-4">
                        <div className="text-sm text-slate-900 tabular-nums">{r.vehicleRegNo ?? "—"}</div>
                      </td>

                      {/* Offer */}
                      <td className="px-4 py-4">
                        <Chip tone={offerTone}>{offerStatus || "—"}</Chip>
                      </td>

                      {/* Req / Pay */}
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-2">
                          <Chip tone={reqTone}>{reqStatus || "—"}</Chip>
                          <Chip tone={payTone}>{payStatus || "—"}</Chip>
                        </div>
                      </td>

                      {/* Fare (no bold) */}
                      <td className="px-4 py-4 text-right">
                        <div className="text-sm text-slate-900 tabular-nums">₹ {money(r.requestFare)}</div>
                      </td>

                      {/* Last activity (no bold) */}
                      <td className="px-4 py-4">
                        <div className="text-xs text-slate-700">{dtClean(lastTs)}</div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <Footer
          page={page}
          size={size}
          total={total}
          totalPages={totalPages}
          setPage={setPage}
          setSize={(n) => {
            setPage(0);
            setSize(n);
          }}
        />
      </div>

      <Drawer open={open} onClose={closeDrawer} row={active} />
    </div>
  );
}
